import { useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import Icon, { FileText, Upload, X } from './Icon';

/**
 * Drag-and-drop / click-to-select PDF upload zone.
 * Controlled: parent owns the selected File object.
 *
 * Props:
 *   file: File | null
 *   onFileChange: (file: File | null) => void
 *   onFileSelected: (file: File) => void  // optional, fires only when a file is chosen
 *   existingFilename: string | null       // label to show when editing an already-attached file
 *   maxSizeMB: number (default 10)
 *   className: string
 */
export default function PdfDropZone({
  file,
  onFileChange,
  onFileSelected,
  existingFilename = null,
  maxSizeMB = 10,
  className,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    if (f.type !== 'application/pdf') return;
    onFileChange(f);
    if (onFileSelected) onFileSelected(f);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFile(e.dataTransfer?.files?.[0]);
  };

  const handleInputChange = (e) => {
    handleFile(e.target.files?.[0]);
  };

  const clear = (e) => {
    e.stopPropagation();
    onFileChange(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const hasSelection = !!file;
  const hasExisting = !file && !!existingFilename;

  return (
    <div
      className={cn(
        'border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all',
        hasSelection
          ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
          : 'border-blue-300 hover:border-blue-400',
        isDragging && !hasSelection && 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
        className
      )}
      onClick={() => fileInputRef.current?.click()}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        onChange={handleInputChange}
        className="hidden"
      />
      {hasSelection ? (
        <div className="relative">
          <button
            type="button"
            onClick={clear}
            className="absolute -top-8 -right-8 p-1.5 rounded-full bg-white border border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300 transition-colors shadow-sm dark:bg-gray-800 dark:border-gray-600 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:border-gray-500"
            title="Remove file"
          >
            <Icon icon={X} size="sm" />
          </button>
          <Icon icon={FileText} size="2xl" className="mx-auto mb-3 text-gray-700 dark:text-gray-300" />
          <p className="font-medium text-gray-900 dark:text-white text-lg">{file.name}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
          <p className="text-xs text-green-600 dark:text-green-400 mt-3">Click to change file</p>
        </div>
      ) : hasExisting ? (
        <div>
          <Icon icon={FileText} size="2xl" className="mx-auto mb-3 text-gray-500 dark:text-gray-400" />
          <p className="font-medium text-gray-900 dark:text-white text-lg">{existingFilename}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Drop a new PDF to replace, or click to browse</p>
        </div>
      ) : (
        <div>
          <Icon icon={Upload} size="2xl" className="mx-auto mb-3 text-gray-400 dark:text-gray-500" />
          <p className="font-medium text-gray-900 dark:text-white text-lg">Drop a PDF here or click to upload</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">PDF files up to {maxSizeMB}MB</p>
        </div>
      )}
    </div>
  );
}
